import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface IconActionProps {
    label: string
    icon: LucideIcon
    onClick?: () => void
    href?: string
    className?: string
    tone?: 'default' | 'whatsapp' | 'danger'
}

const TONES = {
    default: 'text-muted-foreground hover:text-foreground',
    whatsapp: 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700',
    danger: 'text-muted-foreground hover:bg-rose-50 hover:text-rose-600'
}

// Small icon button with a tooltip, used for row actions in lists and tables.
const IconAction = ({ label, icon: Icon, onClick, href, className, tone = 'default' }: IconActionProps): ReactNode => {
    const classes = cn(TONES[tone], className)

    return (
        <Tooltip>
            <TooltipTrigger
                render={href
                    ? <Link href={href} aria-label={label} className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), classes)} />
                    : <Button type='button' variant='ghost' size='icon-sm' onClick={onClick} aria-label={label} className={classes} />}
            >
                <Icon aria-hidden='true' />
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    )
}

export default IconAction
